import { Extension } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, Selection } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';

/** Pagina met eigen tekst (content, plan van aanpak): geen atom, en alinea's als inhoud. */
export function isTextPage(node: ProseMirrorNode): boolean {
  return !node.isAtom && node.type.contentMatch.matchType(node.type.schema.nodes.paragraph) !== null;
}

export function selectionInPage(selection: Selection, pagePos: number): boolean {
  return selection.$from.depth >= 1 && selection.$from.before(1) === pagePos;
}

/**
 * Cursor in een tekstpagina: op `hitPos` (onder de muis) als die in de pagina valt, anders
 * aan het eind van de pagina. Nooit de pagina zelf, want dan vervangt de eerste toets de
 * hele pagina door een losse alinea buiten de pagina, en die wordt niet opgeslagen.
 */
export function caretInTextPage(
  doc: ProseMirrorNode,
  pagePos: number,
  hitPos?: number | null
): Selection | null {
  const page = doc.nodeAt(pagePos);
  if (!page || !isTextPage(page)) return null;
  const end = pagePos + page.nodeSize - 1;
  const candidates = [
    hitPos != null && hitPos > pagePos && hitPos <= end ? Selection.near(doc.resolve(hitPos)) : null,
    Selection.findFrom(doc.resolve(end), -1),
  ];
  return candidates.find((selection) => selection && selectionInPage(selection, pagePos)) ?? null;
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/** Klik op een tekstpagina naast of onder de tekst: de bewerkbare inhoud van die pagina. */
function emptyPageAreaHit(view: EditorView, target: EventTarget | null) {
  if (!(target instanceof Element) || !view.dom.contains(target)) return null;
  // In de tekst zelf, in de paginakop en in velden doet de browser het al goed.
  if (target.closest('[data-node-view-content], [contenteditable="false"], input, textarea, select, button, a')) {
    return null;
  }
  const content = target.closest('[data-block-id]')?.querySelector('[data-node-view-content]');
  if (!content || !view.dom.contains(content)) return null;
  const $content = view.state.doc.resolve(view.posAtDOM(content, 0));
  if ($content.depth < 1 || !isTextPage($content.node(1))) return null;
  return { content, pagePos: $content.before(1) };
}

/**
 * Een pagina is een A4-vlak, maar alleen de tekst erin is bewerkbaar. Klik je eronder of
 * ernaast, dan zette de browser de cursor in het vlak eromheen: wat je typte viel buiten de
 * pagina. Nu komt de cursor op de dichtstbijzijnde regel, zoals in Google Docs.
 */
export const QuotePageCaret = Extension.create({
  name: 'quotePageCaret',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          handleDOMEvents: {
            mousedown: (view, event) => {
              if (event.button !== 0 || !view.editable) return false;
              const hit = emptyPageAreaHit(view, event.target);
              if (!hit) return false;
              const rect = hit.content.getBoundingClientRect();
              const coords = view.posAtCoords({
                left: clamp(event.clientX, rect.left + 1, rect.right - 1),
                top: clamp(event.clientY, rect.top + 1, rect.bottom - 1),
              });
              const selection = caretInTextPage(view.state.doc, hit.pagePos, coords?.pos);
              if (!selection) return false;
              event.preventDefault();
              view.dispatch(view.state.tr.setSelection(selection));
              view.focus();
              return true;
            },
          },
        },
      }),
    ];
  },
});
